/**
 * Admin → ML Forecasting tab (M2 frontend: E4 + F4 + H4).
 *
 * Three sub-tabs over the M2 forecasting platform:
 *   • Dataset Specs (E4) — define WHAT to forecast (source table + time column +
 *     targets + frequency). The platform generates injection-safe pushdown SQL;
 *     Bar B means only a table reference + aggregation query are stored, no rows.
 *   • Runs (F4) — launch a SageMaker job against a spec, with a live worst-case
 *     cost estimate and a cost-guard pre-check server-side. Shows job status.
 *   • Models (H4) — trained models with metrics, forecast/backtest preview, and
 *     a promote lifecycle (candidate → staging → production → archived, admin).
 *
 * All jobs run INSIDE the customer's AWS account via the execution target.
 */
import React, { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { Button, Badge, Card, Input, Select, Modal } from "../../components/ui";
import { mlApi } from "../../api/client";

const FREQUENCIES = [
  ["D", "Daily"], ["W", "Weekly"], ["M", "Monthly"],
  ["Q", "Quarterly"], ["Y", "Yearly"], ["H", "Hourly"],
];
// Exported for unit testing
export const JOB_STATUS_COLOR = {
  succeeded: "#16a34a", running: "#2563eb", submitted: "#7c3aed",
  claimed: "#7c3aed", failed: "#ef4444", cost_blocked: "#ef4444",
};
// Exported for unit testing
export const MODEL_STATUS_COLOR = {
  production: "#16a34a", staging: "#2563eb", candidate: "#7c3aed", archived: COLORS.textMuted || "#6b7280",
};
const err = "#ef4444";

// Exported for unit testing — parse a comma-separated free-text field into a
// trimmed, non-empty list.
export function parseCsv(v) {
  return (v || "").split(",").map((x) => x.trim()).filter(Boolean);
}

// Exported for unit testing — build the source_json envelope for a dataset
// spec. Postgres sources key the container under `schema`; everything else
// (Athena/Glue) keys it under `database`. An empty container is omitted.
export function buildSourceJson(form) {
  const source_json = { kind: form.kind, table: (form.table || "").trim() };
  const container = (form.database || "").trim();
  if (container) source_json[form.kind === "postgres" ? "schema" : "database"] = container;
  return source_json;
}

// Exported for unit testing
export function useList(loader, deps = []) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refresh = async () => {
    setLoading(true); setError("");
    try {
      const r = await loader();
      setRows(Array.isArray(r) ? r : r?.data || []);
    } catch (e) { setError(e?.message || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, deps);
  return { rows, loading, error, refresh, setRows };
}

export default function MLForecastingTab() {
  const [sub, setSub] = useState("specs");
  const TABS = [["specs", "Dataset Specs"], ["runs", "Runs"], ["models", "Models"]];
  return (
    <div style={{ padding: "4px 2px" }} data-testid="ml-forecasting-tab">
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0, color: COLORS.text }}>ML Forecasting</h3>
        <p style={{ margin: "4px 0 0", color: COLORS.textMuted, fontSize: 13, maxWidth: 680 }}>
          Define a dataset, launch time-series forecasts inside your AWS account, and
          promote the best model. Source data never leaves your account (Bar B).
        </p>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, borderBottom: `1px solid ${COLORS.border || "#e5e7eb"}` }}>
        {TABS.map(([id, label]) => (
          <button
            key={id}
            data-testid={`ml-subtab-${id}`}
            onClick={() => setSub(id)}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: "8px 12px",
              fontSize: 14, fontWeight: sub === id ? 600 : 400,
              color: sub === id ? COLORS.primary || "#2563eb" : COLORS.textMuted,
              borderBottom: sub === id ? `2px solid ${COLORS.primary || "#2563eb"}` : "2px solid transparent",
            }}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === "specs" && <SpecsPanel />}
      {sub === "runs" && <RunsPanel />}
      {sub === "models" && <ModelsPanel />}
    </div>
  );
}

// ─── Dataset Specs (E4) ──────────────────────────────────────────────────────

function SpecsPanel() {
  const { rows, loading, error, refresh } = useList(mlApi.listDatasetSpecs);
  const [showCreate, setShowCreate] = useState(false);

  const del = async (id) => {
    try { await mlApi.deleteDatasetSpec(id); refresh(); } catch (e) { alert(e?.message || "Delete failed"); }
  };

  return (
    <div>
      <PanelHeader onRefresh={refresh} onAdd={() => setShowCreate(true)} addLabel="New Spec" addTestId="ml-add-spec-btn" />
      {error && <ErrCard msg={error} />}
      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty testId="ml-specs-empty">No dataset specs yet. Click <strong>New Spec</strong> to define what to forecast.</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((s) => (
            <Card key={s.id} style={{ padding: "14px 16px" }} data-testid="ml-spec-row">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 600, color: COLORS.text }}>{s.name}</span>
                    <Badge color="#7c3aed">{(s.source_json?.kind) || "athena"}</Badge>
                    <Badge color="#2563eb">{s.frequency}</Badge>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMuted }}>
                    time: <code>{s.time_column}</code> · targets: <code>{(s.target_columns || []).join(", ")}</code>
                    {s.grouping_keys?.length ? <> · groups: <code>{s.grouping_keys.join(", ")}</code></> : null}
                  </div>
                </div>
                <Button variant="secondary" onClick={() => del(s.id)} data-testid="ml-spec-delete">Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {showCreate && <CreateSpecModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
    </div>
  );
}

function CreateSpecModal({ onClose, onCreated }) {
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState({
    execution_target_id: "", name: "", kind: "athena", database: "", table: "",
    time_column: "", target_columns: "", frequency: "D", grouping_keys: "", covariates: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [e, setE] = useState("");
  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  useEffect(() => {
    mlApi.listExecutionTargets()
      .then((r) => { const t = Array.isArray(r) ? r : r?.data || []; setTargets(t); if (t[0]) setForm((f) => ({ ...f, execution_target_id: t[0].id })); })
      .catch(() => {});
  }, []);

  const submit = async () => {
    setSubmitting(true); setE("");
    const source_json = buildSourceJson(form);
    try {
      await mlApi.createDatasetSpec({
        execution_target_id: form.execution_target_id, name: form.name.trim(),
        source_json, time_column: form.time_column.trim(), target_columns: parseCsv(form.target_columns),
        frequency: form.frequency, grouping_keys: parseCsv(form.grouping_keys), covariates: parseCsv(form.covariates),
      });
      onCreated();
    } catch (ex) { setE(ex?.message || "Failed to create spec"); setSubmitting(false); }
  };

  return (
    <Modal open onClose={onClose} title="New Dataset Spec" width={560} footer={
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={submitting} data-testid="ml-spec-submit">{submitting ? "Creating…" : "Create Spec"}</Button>
      </div>
    }>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Select label="Execution Target" value={form.execution_target_id} onChange={set("execution_target_id")} data-testid="ml-spec-target">
          {targets.length === 0 && <option value="">No targets — add one first</option>}
          {targets.map((t) => <option key={t.id} value={t.id}>{t.aws_account_id} ({t.connector_type})</option>)}
        </Select>
        <Input label="Name" placeholder="Weekly revenue by region" value={form.name} onChange={set("name")} data-testid="ml-spec-name" />
        <div style={{ display: "flex", gap: 10 }}>
          <Select label="Source" value={form.kind} onChange={set("kind")} data-testid="ml-spec-kind" style={{ flex: 1 }}>
            <option value="athena">Athena / Glue</option>
            <option value="postgres">Postgres (in VPC)</option>
          </Select>
          <Input label={form.kind === "postgres" ? "Schema" : "Database"} placeholder="analytics" value={form.database} onChange={set("database")} data-testid="ml-spec-db" style={{ flex: 1 }} />
        </div>
        <Input label="Table" placeholder="sales_daily" value={form.table} onChange={set("table")} data-testid="ml-spec-table" />
        <div style={{ display: "flex", gap: 10 }}>
          <Input label="Time column" placeholder="order_date" value={form.time_column} onChange={set("time_column")} data-testid="ml-spec-timecol" style={{ flex: 1 }} />
          <Select label="Frequency" value={form.frequency} onChange={set("frequency")} data-testid="ml-spec-freq" style={{ flex: 1 }}>
            {FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </div>
        <Input label="Target column(s)" placeholder="revenue, units  (comma-separated)" value={form.target_columns} onChange={set("target_columns")} data-testid="ml-spec-targets" />
        <Input label="Grouping keys (optional)" placeholder="region, product  (hierarchical)" value={form.grouping_keys} onChange={set("grouping_keys")} data-testid="ml-spec-groups" />
        {e && <div style={{ color: err, fontSize: 13 }} data-testid="ml-spec-error">{e}</div>}
      </div>
    </Modal>
  );
}

// ─── Runs (F4) ───────────────────────────────────────────────────────────────

function RunsPanel() {
  const { rows, loading, error, refresh } = useList(mlApi.listJobs);
  const [showCreate, setShowCreate] = useState(false);
  return (
    <div>
      <PanelHeader onRefresh={refresh} onAdd={() => setShowCreate(true)} addLabel="New Run" addTestId="ml-add-run-btn" />
      {error && <ErrCard msg={error} />}
      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty testId="ml-runs-empty">No runs yet. Click <strong>New Run</strong> to launch a forecast.</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((j) => (
            <Card key={j.id} style={{ padding: "14px 16px" }} data-testid="ml-run-row">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Badge color={JOB_STATUS_COLOR[j.status] || COLORS.textMuted}>{j.status}</Badge>
                    <span style={{ fontWeight: 600, color: COLORS.text }}>{j.job_type}</span>
                    <span style={{ fontSize: 12, color: COLORS.textMuted }}>{j.method || "auto"}</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMuted, fontFamily: "monospace", overflowWrap: "anywhere" }}>
                    {j.id}
                  </div>
                </div>
                {j.error ? <span style={{ color: err, fontSize: 12, maxWidth: 260 }}>{j.error}</span> : null}
              </div>
            </Card>
          ))}
        </div>
      )}
      {showCreate && <CreateRunModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
    </div>
  );
}

function CreateRunModal({ onClose, onCreated }) {
  const [specs, setSpecs] = useState([]);
  const [form, setForm] = useState({
    dataset_spec_id: "", job_type: "forecast", method: "", horizon: 6,
    instance_type: "ml.m5.large", instance_count: 1, max_runtime_seconds: 1800,
  });
  const [cost, setCost] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [e, setE] = useState("");
  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  useEffect(() => {
    mlApi.listDatasetSpecs().then((r) => { const s = Array.isArray(r) ? r : r?.data || []; setSpecs(s); if (s[0]) setForm((f) => ({ ...f, dataset_spec_id: s[0].id })); }).catch(() => {});
  }, []);

  // Live worst-case cost estimate whenever the instance shape changes.
  useEffect(() => {
    let live = true;
    mlApi.costEstimate({ instance_type: form.instance_type, instance_count: form.instance_count, max_runtime_seconds: form.max_runtime_seconds })
      .then((c) => { if (live) setCost(c); }).catch(() => { if (live) setCost(null); });
    return () => { live = false; };
  }, [form.instance_type, form.instance_count, form.max_runtime_seconds]);

  const submit = async () => {
    setSubmitting(true); setE("");
    try {
      await mlApi.createJob({
        dataset_spec_id: form.dataset_spec_id, job_type: form.job_type,
        method: form.method.trim() || null, horizon: Number(form.horizon),
        instance_type: form.instance_type, instance_count: Number(form.instance_count),
        max_runtime_seconds: Number(form.max_runtime_seconds),
      });
      onCreated();
    } catch (ex) { setE(ex?.message || "Failed to launch run"); setSubmitting(false); }
  };

  return (
    <Modal open onClose={onClose} title="New Forecast Run" width={540} footer={
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: COLORS.textMuted }} data-testid="ml-run-cost">
          {cost ? <>est. ≤ <strong style={{ color: COLORS.text }}>${cost.estimated_cost_usd}</strong> ({cost.hourly_usd}/hr)</> : "—"}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !form.dataset_spec_id} data-testid="ml-run-submit">{submitting ? "Launching…" : "Launch"}</Button>
        </div>
      </div>
    }>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Select label="Dataset Spec" value={form.dataset_spec_id} onChange={set("dataset_spec_id")} data-testid="ml-run-spec">
          {specs.length === 0 && <option value="">No specs — create one first</option>}
          {specs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </Select>
        <div style={{ display: "flex", gap: 10 }}>
          <Select label="Job type" value={form.job_type} onChange={set("job_type")} data-testid="ml-run-type" style={{ flex: 1 }}>
            {["forecast", "backtest", "train", "evaluate", "auto"].map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Input label="Method (blank = AutoML)" placeholder="auto · gbt · auto_arima · chronos · lstm …" value={form.method} onChange={set("method")} data-testid="ml-run-method" style={{ flex: 1 }} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Input label="Horizon" type="number" value={form.horizon} onChange={set("horizon")} data-testid="ml-run-horizon" style={{ flex: 1 }} />
          <Select label="Instance" value={form.instance_type} onChange={set("instance_type")} data-testid="ml-run-instance" style={{ flex: 2 }}>
            <optgroup label="CPU">
              {["ml.t3.medium", "ml.m5.large", "ml.m5.xlarge", "ml.m5.2xlarge", "ml.c5.xlarge", "ml.c5.2xlarge"].map((i) => <option key={i} value={i}>{i}</option>)}
            </optgroup>
            <optgroup label="GPU (requires ML Governance → GPU enabled)">
              {["ml.g4dn.xlarge", "ml.g4dn.2xlarge", "ml.g5.xlarge", "ml.g5.2xlarge"].map((i) => <option key={i} value={i}>{i}</option>)}
            </optgroup>
          </Select>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Input label="Instance count" type="number" value={form.instance_count} onChange={set("instance_count")} data-testid="ml-run-count" style={{ flex: 1 }} />
          <Input label="Max runtime (s)" type="number" value={form.max_runtime_seconds} onChange={set("max_runtime_seconds")} data-testid="ml-run-runtime" style={{ flex: 1 }} />
        </div>
        {e && <div style={{ color: err, fontSize: 13 }} data-testid="ml-run-error">{e}</div>}
      </div>
    </Modal>
  );
}

// ─── Models (H4) ─────────────────────────────────────────────────────────────

// Exported for unit testing
export const NEXT_STATUS = { candidate: "staging", staging: "production" };

function ModelsPanel({ isAdmin = true }) {
  const { rows, loading, error, refresh } = useList(mlApi.listModels);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({}); // id -> { forecasts, backtests }

  const promote = async (id, to) => {
    try { await mlApi.promoteModel(id, to); refresh(); } catch (e) { alert(e?.message || "Promote failed"); }
  };
  const toggle = async (id) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!detail[id]) {
      try {
        const [forecasts, backtests] = await Promise.all([mlApi.getModelForecasts(id), mlApi.getModelBacktests(id)]);
        setDetail((d) => ({ ...d, [id]: { forecasts: forecasts || [], backtests: backtests || [] } }));
      } catch { setDetail((d) => ({ ...d, [id]: { forecasts: [], backtests: [] } })); }
    }
  };

  return (
    <div>
      <PanelHeader onRefresh={refresh} />
      {error && <ErrCard msg={error} />}
      {loading ? <Loading /> : rows.length === 0 ? (
        <Empty testId="ml-models-empty">No models yet. A successful run produces a model here.</Empty>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((m) => (
            <Card key={m.id} style={{ padding: "14px 16px" }} data-testid="ml-model-row">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Badge color={MODEL_STATUS_COLOR[m.status] || COLORS.textMuted}>{m.status}</Badge>
                    <span style={{ fontWeight: 600, color: COLORS.text }}>{m.method || m.algorithm || "model"}</span>
                    {m.metrics?.MASE != null && <span style={{ fontSize: 12, color: COLORS.textMuted }}>MASE {m.metrics.MASE}</span>}
                    {m.metrics?.sMAPE != null && <span style={{ fontSize: 12, color: COLORS.textMuted }}>sMAPE {m.metrics.sMAPE}</span>}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, color: COLORS.textMuted, fontFamily: "monospace", overflowWrap: "anywhere" }}>{m.id}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="secondary" onClick={() => toggle(m.id)} data-testid="ml-model-expand">{expanded === m.id ? "Hide" : "Results"}</Button>
                  {isAdmin && NEXT_STATUS[m.status] && (
                    <Button onClick={() => promote(m.id, NEXT_STATUS[m.status])} data-testid="ml-model-promote">
                      Promote → {NEXT_STATUS[m.status]}
                    </Button>
                  )}
                  {isAdmin && m.status !== "archived" && (
                    <Button variant="secondary" onClick={() => promote(m.id, "archived")} data-testid="ml-model-archive">Archive</Button>
                  )}
                </div>
              </div>
              {expanded === m.id && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${COLORS.border || "#e5e7eb"}`, paddingTop: 10 }} data-testid="ml-model-detail">
                  {!detail[m.id] ? <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Loading results…</span> : (
                    <>
                      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>
                        {detail[m.id].forecasts.length} forecasts · {detail[m.id].backtests.length} backtests
                      </div>
                      <ForecastPreview rows={detail[m.id].forecasts.slice(0, 8)} />
                    </>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ForecastPreview({ rows }) {
  if (!rows.length) return <span style={{ color: COLORS.textMuted, fontSize: 13 }}>No forecast rows.</span>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 12, width: "100%" }}>
        <thead>
          <tr style={{ color: COLORS.textMuted, textAlign: "left" }}>
            <th style={{ padding: "4px 8px" }}>series</th><th style={{ padding: "4px 8px" }}>ts</th>
            <th style={{ padding: "4px 8px" }}>yhat</th><th style={{ padding: "4px 8px" }}>lower</th><th style={{ padding: "4px 8px" }}>upper</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${COLORS.border || "#eee"}` }}>
              <td style={{ padding: "4px 8px" }}>{r.series_key ?? "—"}</td>
              <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{r.ts}</td>
              <td style={{ padding: "4px 8px", fontVariantNumeric: "tabular-nums" }}>{r.yhat}</td>
              <td style={{ padding: "4px 8px", fontVariantNumeric: "tabular-nums", color: COLORS.textMuted }}>{r.yhat_lower}</td>
              <td style={{ padding: "4px 8px", fontVariantNumeric: "tabular-nums", color: COLORS.textMuted }}>{r.yhat_upper}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── shared bits ─────────────────────────────────────────────────────────────

function PanelHeader({ onRefresh, onAdd, addLabel, addTestId }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
      <Button variant="secondary" onClick={onRefresh}>Refresh</Button>
      {onAdd && <Button onClick={onAdd} data-testid={addTestId}>{addLabel}</Button>}
    </div>
  );
}
const Loading = () => <Card><span style={{ color: COLORS.textMuted }}>Loading…</span></Card>;
const ErrCard = ({ msg }) => <Card style={{ borderColor: err, marginBottom: 12 }}><span style={{ color: err }}>{msg}</span></Card>;
const Empty = ({ children, testId }) => (
  <Card><div style={{ textAlign: "center", padding: "24px 0", color: COLORS.textMuted }} data-testid={testId}>{children}</div></Card>
);
